import type {SetStateAction} from 'react'
import {
  type CommentsSortConfig,
  useCommentsSortConfig,
  validOrDefaultCommentsSortConfig,
} from '@github-ui/commenting/useCommentsSortPreference'
import type {CommentsDisplayMode} from './utils/display-mode'

export const CommentsSort = {
  NewestFirst: 'newest-first',
  OldestFirst: 'oldest-first',
  DiffOrder: 'diff-order',
} as const

export type CommentsSort = (typeof CommentsSort)[keyof typeof CommentsSort]

/** Only the flat thread display mode can be sorted by diff/code order. */
export const supportsCodeOrderSort = (displayMode: CommentsDisplayMode) => displayMode === 'review-threads'

function resolveSortForDisplayMode(displayMode: CommentsDisplayMode, config: CommentsSortConfig): CommentsSort {
  if (config.codeOrderOverride && supportsCodeOrderSort(displayMode)) return CommentsSort.DiffOrder

  return config.dateOrder === 'ascending' ? CommentsSort.OldestFirst : CommentsSort.NewestFirst
}

/**
 * Tracks the state of comments sort direction.
 * @param localStorageKey Optional unique ID for local storage keying, so that separate instances can key separately
 * if desired. If not provided, will share the default local storage destination.
 */
export function useCommentsSortState(displayMode: CommentsDisplayMode, localStorageKey?: string) {
  const [unvalidatedSortConfig, setUnvalidatedSortConfig] = useCommentsSortConfig(localStorageKey)

  const setSort = (action: SetStateAction<CommentsSort>) => {
    setUnvalidatedSortConfig(current => {
      const validCurrentConfig = validOrDefaultCommentsSortConfig(current)
      const currentSort = resolveSortForDisplayMode(displayMode, validCurrentConfig)

      const newSort = typeof action === 'function' ? action(currentSort) : action

      // Diff order always preserves chrono order selection for unsupported views
      if (newSort === CommentsSort.DiffOrder) return {...validCurrentConfig, codeOrderOverride: true}

      return {
        ...validCurrentConfig,
        // Chrono order overrides diff order on supported views, but preserves diff order preference if set from an unsupported view
        ...(supportsCodeOrderSort(displayMode) ? {codeOrderOverride: false} : {}),
        dateOrder: newSort === CommentsSort.NewestFirst ? 'descending' : 'ascending',
      }
    })
  }

  // don't rely on internal useLocalStorage types which are just casting on parse
  const sort = resolveSortForDisplayMode(displayMode, validOrDefaultCommentsSortConfig(unvalidatedSortConfig))

  return [sort, setSort] as const
}
